/* global sessionStorage */
import React from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';

import { useMergeState } from '@app/hooks';
import { SEARCH_SCOPES } from '@app/services/constants/algolia';
import { mapDispatchToProps } from '@app/store/map-dispatch-to-props';

import { CartUtilityNav } from './cart';
import { DesktopUtilityNav } from './desktop';
import { DesktopUtilitySearch } from './desktop-utility-search';
import { MobileUtilityNav } from './mobile';

const mobileElement = document.querySelector(
	'[data-react-component-id="utility-nav-mobile"]',
);

/* eslint-disable react/jsx-props-no-spreading */
const MobileUtilityNavPortal = (props) =>
	createPortal(<MobileUtilityNav {...props} />, mobileElement);
/* eslint-enable react/jsx-props-no-spreading */

const UtilityNavRaw = ({ auth, cart, actions }) => {
	const initialState = {
		isLoaded: false,
		authText: 'Loading...',
		isMobileSearchOpen: false,
	};
	const [state, mergeState] = useMergeState(initialState);

	const { isLoaded, authText } = state;

	const { user, isAuthenticated } = auth;
	const { cartSummary } = cart;

	React.useEffect(() => {
		async function getData() {
			const [authResponse] = await Promise.all([
				actions.isAuthenticated(),
				actions.getUserInfo(),
				actions.getLoginInfo(),
				actions.getTiBaseUrl(),
			]);

			const urlPath = window.location.pathname.split('/')[1];

			const isNotCartFlow =
				urlPath !== 'cart' &&
				urlPath !== 'checkout' &&
				urlPath !== 'confirmation';
			const isCartConfirmation = urlPath === 'confirmation';

			if (authResponse.payload) {
				actions.setCartOwner();

				await actions.getContact();
				await actions.linkBillableAccountToCart();

				if (isNotCartFlow) {
					await actions.getCartSummary();

					return mergeState({
						isLoaded: true,
					});
				}

				if (isCartConfirmation) {
					await actions.getOrder();
				} else {
					await actions.getCart(!isNotCartFlow ? 'page' : 'silent');
				}

				return mergeState({
					isLoaded: true,
				});
			}

			if (isNotCartFlow) {
				await actions.getCartSummary();

				return mergeState({
					isLoaded: true,
				});
			}

			if (isCartConfirmation) {
				await actions.getOrder();
			} else {
				await actions.getCart(!isNotCartFlow ? 'page' : 'silent');
			}

			return mergeState({
				isLoaded: true,
			});
		}

		getData();
	}, [actions, mergeState]);

	React.useEffect(() => {
		mergeState({
			authText: (() => {
				switch (true) {
					case isLoaded && isAuthenticated:
						return `Hi, ${user.firstName}`;
					case isLoaded && !isAuthenticated:
						return 'Sign In';
					default:
						return 'Loading...';
				}
			})(),
		});
	}, [isLoaded, user.firstName, isAuthenticated, mergeState]);

	/* global gtag */
	React.useEffect(() => {
		if (user.contactSeq && isAuthenticated && sessionStorage.getItem('sentLoginEvent') !== 'true') {
				const preferredEmail = user.emails.find((email) => email.preferred)?.email;
				gtag('event', 'user_logged_in', {
					student_id: user.contactSeq,
					primary_email: preferredEmail,
				});
			sessionStorage.setItem('sentLoginEvent', 'true');
		}
	}, [user.contactSeq, isAuthenticated, user.emails]);

	return (
		<>
			<DesktopUtilitySearch searchModes={SEARCH_SCOPES} />

			<CartUtilityNav
				cartItemsLength={cartSummary.itemCount}
				isLoaded={isLoaded}
				state={state}
				mergeState={mergeState}
				searchModes={SEARCH_SCOPES}
			/>
			<DesktopUtilityNav
				auth={auth}
				actions={actions}
				authText={authText}
				searchModes={SEARCH_SCOPES}
			/>

			<MobileUtilityNavPortal
				auth={auth}
				actions={actions}
				authText={authText}
				searchModes={SEARCH_SCOPES}
			/>
		</>
	);
};

const mapStateToProps = ({ app, auth, cart }) => ({
	app,
	auth,
	cart,
});

export const UtilityNav = connect(
	mapStateToProps,
	mapDispatchToProps,
)(UtilityNavRaw);

export default UtilityNav;
